import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, Mail } from 'lucide-react';

export default function MFAVerify() {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const refs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.userId;
  const email = location.state?.email;

  useEffect(() => {
    if (!userId) navigate('/');
    refs.current[0]?.focus();
  }, []);

  const handleChange = (val, idx) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[idx] = val;
    setDigits(next);
    if (val && idx < 5) refs.current[idx + 1]?.focus();
    if (val && idx === 5 && next.every(d => d !== '')) {
      handleVerify(next.join(''));
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setDigits(paste.split(''));
      handleVerify(paste);
    }
  };

  const handleVerify = async (code) => {
    setVerifying(true);
    try {
      const { data } = await axios.post( `${process.env.REACT_APP_BACKEND_URL}/verify-otp`, { userId: userId, code, });
      if (data.verified) {
        localStorage.setItem('session_id', data.sessionId);
        toast.success('Verified! Welcome.');
        navigate('/pricingpage', { state: { sessionId: data.sessionId } });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
      setDigits(['', '', '', '', '', '']);
      refs.current[0]?.focus();
      //await handleResend(); // optionally resend automatically
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/send-otp`, { userId: userId, email,});
      toast.success(`New code sent to ${email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  const token = digits.join('');

  return (
    <div style={styles.page}>
      <div style={styles.glow1} />
      <div style={styles.glow2} />

      <div style={styles.card}>
        <div style={styles.iconWrap}>
          <Mail size={28} color="#fff" />
        </div>

        <h1 style={styles.title}>Check your email</h1>
        <p style={styles.subtitle}>
          We sent a 6-digit code to{' '}
          <strong style={{ color: '#fff' }}>{email}</strong>.
          Enter it below.
        </p>

        <div style={styles.digitsRow} onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={el => refs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={e => handleChange(e.target.value, i)}
              onKeyDown={e => handleKeyDown(e, i)}
              style={{
                ...styles.digitInput,
                borderColor: d ? 'rgba(124,58,237,0.8)' : 'rgba(255,255,255,0.15)',
                background: d ? 'rgba(124,58,237,0.15)' : 'rgba(255,255,255,0.05)',
              }}
            />
          ))}
        </div>

        <button
          onClick={() => token.length === 6 && handleVerify(token)}
          disabled={verifying || token.length < 6}
          style={{
            ...styles.btn,
            opacity: token.length < 6 ? 0.5 : 1,
            cursor: token.length < 6 ? 'not-allowed' : 'pointer',
          }}
        >
          {verifying ? <Loader2 size={18} style={styles.spin} /> : 'Verify & Sign In'}
        </button>

        <button
          onClick={handleResend}
          disabled={resending}
          style={styles.resendBtn}
        >
          {resending ? 'Sending...' : "Didn't get it? Resend code"}
        </button>

        <button onClick={() => navigate('/')} style={styles.backBtn}>
          Back to sign in
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'DM Sans', sans-serif",
  },
  glow1: {
    position: 'absolute',
    top: -100,
    right: -100,
    width: 400,
    height: 400,
    background: 'rgba(124, 58, 237, 0.3)',
    borderRadius: '50%',
    filter: 'blur(120px)',
    pointerEvents: 'none',
  },
  glow2: {
    position: 'absolute',
    bottom: -100,
    left: -100,
    width: 400,
    height: 400,
    background: 'rgba(74, 222, 128, 0.15)',
    borderRadius: '50%',
    filter: 'blur(120px)',
    pointerEvents: 'none',
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 24,
    padding: '40px 36px',
    width: '100%',
    maxWidth: 420,
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
  },
  iconWrap: {
    width: 56,
    height: 56,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #7c3aed, #4ade80)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#fff',
    margin: '0 0 10px',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.55)',
    lineHeight: 1.6,
    margin: '0 0 32px',
  },
  digitsRow: {
    display: 'flex',
    gap: 10,
    justifyContent: 'center',
    marginBottom: 28,
  },
  digitInput: {
    width: 48,
    height: 56,
    borderRadius: 12,
    border: '1px solid',
    fontSize: 22,
    fontWeight: 700,
    color: '#fff',
    textAlign: 'center',
    outline: 'none',
    transition: 'all 0.15s ease',
    fontFamily: 'monospace',
  },
  btn: {
    width: '100%',
    background: 'linear-gradient(90deg, #7c3aed, #4ade80)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    padding: '14px 20px',
    fontSize: 15,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 16,
    cursor: 'pointer',
  },
  resendBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.6)',
    fontSize: 14,
    cursor: 'pointer',
    padding: '8px 0',
    display: 'block',
    width: '100%',
    marginBottom: 8,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.3)',
    fontSize: 13,
    cursor: 'pointer',
    padding: 0,
  },
  spin: {
    animation: 'spin 1s linear infinite',
  },
};
